import { useMutation } from '@tanstack/react-query';
import BigNumber from 'bignumber.js';
import { z } from 'zod';
import { createCompanySettings, updateCompanySettings } from '~/api/company-settings';
import { queryClient } from '~/client/queryClient';
import { useActiveCompany } from '~/hooks/useActiveCompany';
import { useAnalytics } from '~/hooks/useAnalytics';
import useExceptionLogger from '~/hooks/useExceptionLogger';
import { AnalyticsEvents } from '~/services/analytics';
import { useAllBookkeepingAccountsQuery } from '~/services/bookkeeping/bookkeeping-accounts';
import { Company, CompanyQueryKeys } from '~/types/company';
import {
  BookkeepingAccountsSettings,
  BookkeepingIntraSettings,
  BookkeepingSettings,
  InvoicingSettings,
  VATPeriodAllocationLogic,
} from '~/types/company-settings';
import { WithholdingTaxType } from '~/types/document';
import { FlowPaymentTerms, PaymentMethod } from '~/types/flow';
import { naturaValues, StampType, TipoCassa } from '~/types/invoicing';
import { UserQueryKeys } from '~/types/user';

const baseDefaults: InvoicingSettings = {
  payment: {
    accountId: null,
    institutionName: null,
    paymentMethod: PaymentMethod.Transfer,
    paymentAccount: '',
    paymentDate: null,
  },
  socialSecurity: null,
  vat: { percentage: '22', taxExemption: null },
  withholdingTax: [],
  stamp: null,
  showPlus15PaymentTerms: false,
};

/** Mirrors the backend default for `bookkeeping.fixed_asset.book_depreciation_follows_tax_rules`. */
const BOOK_DEPRECIATION_FOLLOWS_TAX_RULES_DEFAULT = true;

const percentageSchema = z.string().transform((n) => BigNumber(n).toFixed());
const nullableIntegerSchema = z.preprocess(
  (value) => (value === '' || value === undefined ? null : value),
  z.coerce.number().int().nullable(),
);

export const invoicingSettingsSchema = z.object({
  payment: z.object({
    accountId: z.string().nullable(),
    institutionName: z.string().nullable(),
    paymentDate: z.nativeEnum(FlowPaymentTerms).nullable(),
    paymentMethod: z.nativeEnum(PaymentMethod),
    paymentAccount: z.string().nullable(),
  }),
  socialSecurity: z
    .object({
      amountPercentage: percentageSchema.default('0'),
      percentage: percentageSchema.default('0'),
      type: z.nativeEnum(TipoCassa),
    })
    .nullable(),
  stamp: z.object({ type: z.nativeEnum(StampType) }).nullable(),
  vat: z.object({
    percentage: percentageSchema,
    taxExemption: z.enum(naturaValues).nullable(),
  }),
  withholdingTax: z.array(
    z.object({
      amountPercentage: percentageSchema,
      percentage: percentageSchema,
      motivation: z.string(),
      type: z.nativeEnum(WithholdingTaxType),
    }),
  ),
  showPlus15PaymentTerms: z.boolean().default(false),
});

export type InvoicingSettingsFormInput = z.input<typeof invoicingSettingsSchema>;
export type InvoicingSettingsFormOutput = z.output<typeof invoicingSettingsSchema>;

const bookkeepingSettingsSchema = z.object({
  accounts: z
    .object({
      debtsSuppliers: z.string().nullable(),
      debtsForeignSuppliers: z.string().nullable(),
      creditsClients: z.string().nullable(),
      creditsForeignClients: z.string().nullable(),
      vatSales: z.string().nullable(),
      vatPurchases: z.string().nullable(),
      bankFees: z.string().nullable(),
      billsCash: z.string().nullable(),
      billsCashContanti: z.string().nullable(),
      billsCashElectronic: z.string().nullable(),
      billsCashTicket: z.string().nullable(),
      billsRevenueTicket: z.string().nullable(),
      billsUnredeemedGift: z.string().nullable(),
      vatBills: z.string().nullable(),
      netBills: z.string().nullable(),
      revenueAccount: z.string().nullable(),
      withholdingAccount: z.string().nullable(),
      withholdingAccountSales: z.string().nullable(),
      activeRoundingAccount: z.string().nullable(),
      passiveRoundingAccount: z.string().nullable(),
      vatSalesSplitPayment: z.string().nullable(),
      previousYearVatCredit: z.string().nullable(),
      vatSettlement: z.string().nullable(),
      quarterlyVatInterest: z.string().nullable(),
      socialSecuritySales: z.string().nullable(),
      taxReturnVatRefund: z.string().nullable().default(null),
      taxReturnVatCompensation: z.string().nullable().default(null),
      accruedIncome: z.string().nullable().default(null),
      accruedExpense: z.string().nullable().default(null),
      prepaidExpense: z.string().nullable().default(null),
      deferredRevenue: z.string().nullable().default(null),
      invoicesToIssue: z.string().nullable().default(null),
      invoicesToReceive: z.string().nullable().default(null),
      creditNotesToIssue: z.string().nullable().default(null),
      creditNotesToReceive: z.string().nullable().default(null),
    })
    .nullable(),
  vatPeriodAllocationLogic: z.nativeEnum(VATPeriodAllocationLogic).default(VATPeriodAllocationLogic.ReceiptDate),
  lipe: z
    .object({
      controllingEntityVatNumber: z.string().nullable(),
      groupVatSettlement: z.boolean().nullable(),
      declarantFirstName: z.string().nullable().default(null),
      declarantLastName: z.string().nullable().default(null),
      declarantGender: z.enum(['MALE', 'FEMALE']).nullable().default(null),
      declarantBirthDate: z.string().nullable().default(null),
      declarantBirthPlace: z.string().nullable().default(null),
      declarantBirthProvince: z.string().nullable().default(null),
      declarantFiscalCode: z.string().nullable(),
      chargeCode: z.string().nullable(),
      declarantCompanyFiscalCode: z.string().nullable(),
      submissionCommitmentCode: z.enum(['1', '2']).nullable(),
      intermediaryFiscalCode: z.string().nullable(),
    })
    .nullable(),
  intra: z
    .object({
      salesServiceCode: nullableIntegerSchema.default(null),
    })
    .nullable()
    .optional(),
  fixedAsset: z
    .object({
      bookDepreciationFollowsTaxRules: z.boolean().nullable().default(BOOK_DEPRECIATION_FOLLOWS_TAX_RULES_DEFAULT),
    })
    .nullable()
    .optional(),
});

export const useInvoicingSettings = (): InvoicingSettings => {
  const company = useActiveCompany();
  const parsedSettings = invoicingSettingsSchema.safeParse(company.companySettings?.invoicing);
  if (parsedSettings.success) {
    return parsedSettings.data;
  }

  return baseDefaults;
};

/**
 * Whether the EOM+15 payment-term options are enabled for the active company.
 * Read straight from the stored invoicing settings (not the form schema) so the
 * invoicing-settings form save never clobbers it — {@link useUpdateInvoicingSettings}
 * merges any update over the current invoicing object, preserving this flag.
 */
export const useShowPlus15PaymentTerms = (): boolean => {
  const company = useActiveCompany();
  return company.companySettings?.invoicing?.showPlus15PaymentTerms ?? false;
};

export const useBookkeepingSettings = (): BookkeepingSettings | null => {
  const company = useActiveCompany();
  const bookkeepingSettings = company.companySettings?.bookkeeping;

  if (!bookkeepingSettings) {
    return null;
  }

  const parsedSettings = bookkeepingSettingsSchema.safeParse(bookkeepingSettings);
  if (parsedSettings.success) {
    return parsedSettings.data;
  }

  return null;
};

/**
 * Whether the first-year book depreciation of the active company follows the fiscal rules.
 * Defaults to `true`, matching the backend, for companies that predate the flag.
 */
export const useBookDepreciationFollowsTaxRules = (): boolean => {
  const company = useActiveCompany();
  return (
    company.companySettings?.bookkeeping?.fixedAsset?.bookDepreciationFollowsTaxRules ??
    BOOK_DEPRECIATION_FOLLOWS_TAX_RULES_DEFAULT
  );
};

export const useBookkeepingIntraSalesServiceCode = (): BookkeepingIntraSettings['salesServiceCode'] => {
  const company = useActiveCompany();
  const parsedServiceCode = nullableIntegerSchema.safeParse(
    company.companySettings?.bookkeeping?.intra?.salesServiceCode,
  );

  return parsedServiceCode.success ? parsedServiceCode.data : null;
};

const useBookkeepingAccountsMapper = () => {
  const { data: allAccounts } = useAllBookkeepingAccountsQuery({ hasChildren: false });

  return (accountId: string | null | undefined) => {
    if (!accountId) {
      return null;
    }
    const account = allAccounts?.find((a) => a.id === accountId);
    return account?.code ?? null;
  };
};

const upsertCompanySettingsBookkeeping = async (company: Company, bookkeeping: BookkeepingSettings) => {
  const currentSettings = company.companySettings;
  if (!currentSettings) {
    await createCompanySettings(company.id, { bookkeeping });
  } else {
    await updateCompanySettings(currentSettings.id, { bookkeeping });
  }
};

export const useUpsertBookkeepingLipeSettings = () => {
  const company = useActiveCompany();

  return useMutation({
    mutationFn: async (settings: Partial<NonNullable<BookkeepingSettings['lipe']>>) => {
      const currentSettings = company.companySettings;
      const currentLIPESettings = currentSettings?.bookkeeping?.lipe;
      const bookkeeping: BookkeepingSettings = {
        accounts: null,
        vatPeriodAllocationLogic: null,
        ...currentSettings?.bookkeeping,
        lipe: {
          controllingEntityVatNumber: null,
          groupVatSettlement: null,
          declarantFirstName: null,
          declarantLastName: null,
          declarantGender: null,
          declarantBirthDate: null,
          declarantBirthPlace: null,
          declarantBirthProvince: null,
          declarantFiscalCode: null,
          chargeCode: null,
          declarantCompanyFiscalCode: null,
          submissionCommitmentCode: null,
          intermediaryFiscalCode: null,
          ...currentLIPESettings,
          ...settings,
        },
      };
      await upsertCompanySettingsBookkeeping(company, bookkeeping);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: UserQueryKeys.currentUser });
      queryClient.invalidateQueries({ queryKey: CompanyQueryKeys.allCompanies });
    },
  });
};

export const useUpsertBookkeepingFixedAssetSettings = () => {
  const company = useActiveCompany();
  const { captureException } = useExceptionLogger();

  return useMutation({
    mutationFn: async (settings: Partial<NonNullable<BookkeepingSettings['fixedAsset']>>) => {
      const currentSettings = company.companySettings;
      const bookkeeping: BookkeepingSettings = {
        accounts: null,
        vatPeriodAllocationLogic: null,
        lipe: null,
        ...currentSettings?.bookkeeping,
        fixedAsset: {
          bookDepreciationFollowsTaxRules: BOOK_DEPRECIATION_FOLLOWS_TAX_RULES_DEFAULT,
          ...currentSettings?.bookkeeping?.fixedAsset,
          ...settings,
        },
      };
      await upsertCompanySettingsBookkeeping(company, bookkeeping);
    },
    onError: (error) => {
      captureException(error);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: UserQueryKeys.currentUser });
      queryClient.invalidateQueries({ queryKey: CompanyQueryKeys.allCompanies });
    },
  });
};

export const useUpsertBookkeepingSettingsAccounts = () => {
  const company = useActiveCompany();
  const mapAccountIdToCode = useBookkeepingAccountsMapper();

  return useMutation({
    mutationFn: async (accounts: Partial<BookkeepingAccountsSettings>) => {
      const currentSettings = company.companySettings;
      const currentAccounts = currentSettings?.bookkeeping?.accounts;

      // Sending only the accounts that have a real code lets a single
      // account be set even when the company is fully unconfigured.
      const mergedAccounts: Partial<BookkeepingAccountsSettings> = { ...currentAccounts };
      for (const [key, value] of Object.entries(accounts)) {
        mergedAccounts[key as keyof BookkeepingAccountsSettings] = mapAccountIdToCode(value);
      }
      const cleanedAccounts = Object.fromEntries(
        Object.entries(mergedAccounts).filter(([, value]) => value != null),
      ) as Partial<BookkeepingAccountsSettings>;
      const bookkeeping: BookkeepingSettings = {
        ...currentSettings?.bookkeeping,
        accounts: cleanedAccounts as BookkeepingAccountsSettings,
        vatPeriodAllocationLogic:
          currentSettings?.bookkeeping?.vatPeriodAllocationLogic ?? VATPeriodAllocationLogic.ReceiptDate,
        lipe: currentSettings?.bookkeeping?.lipe ?? {
          controllingEntityVatNumber: null,
          groupVatSettlement: null,
          declarantFirstName: null,
          declarantLastName: null,
          declarantGender: null,
          declarantBirthDate: null,
          declarantBirthPlace: null,
          declarantBirthProvince: null,
          declarantFiscalCode: null,
          chargeCode: null,
          declarantCompanyFiscalCode: null,
          submissionCommitmentCode: null,
          intermediaryFiscalCode: null,
        },
      };
      await upsertCompanySettingsBookkeeping(company, bookkeeping);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: UserQueryKeys.currentUser });
      queryClient.invalidateQueries({ queryKey: CompanyQueryKeys.allCompanies });
    },
  });
};

export const useUpdateInvoicingSettings = () => {
  const company = useActiveCompany();
  return useMutation({
    mutationFn: async (values: Partial<InvoicingSettings>) => {
      const currentSettings = company.companySettings;
      if (!currentSettings) {
        await createCompanySettings(company.id, { invoicing: { ...baseDefaults, ...values } });
      } else {
        await updateCompanySettings(currentSettings.id, {
          invoicing: { ...currentSettings.invoicing, ...values } as InvoicingSettings,
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: UserQueryKeys.currentUser });
      queryClient.invalidateQueries({ queryKey: CompanyQueryKeys.allCompanies });
    },
  });
};

export const useAutocategorizationSettings = () => {
  const company = useActiveCompany();
  return company.companySettings?.autocategorization;
};

export const useUpdateAutocategorizationSettings = () => {
  const company = useActiveCompany();
  const { track } = useAnalytics();
  return useMutation({
    mutationFn: async (value: boolean) => {
      const currentSettings = company.companySettings;
      if (!currentSettings) {
        await createCompanySettings(company.id, { autocategorization: value });
      } else {
        await updateCompanySettings(currentSettings.id, { autocategorization: value });
      }
    },
    onSuccess: (_data, value) => {
      track(AnalyticsEvents.AUTOCATEGORIZATION_STATUS_CHANGED, {
        status: value ? 'enabled' : 'disabled',
      });
      queryClient.invalidateQueries({ queryKey: UserQueryKeys.currentUser });
      queryClient.invalidateQueries({ queryKey: CompanyQueryKeys.allCompanies });
    },
  });
};

export const useVATPeriodAllocationLogic = (): VATPeriodAllocationLogic => {
  const company = useActiveCompany();
  return company.companySettings?.bookkeeping?.vatPeriodAllocationLogic ?? VATPeriodAllocationLogic.ReceiptDate;
};
